import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationIcon from '../Notifications/NotificationIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { API_URL, getAvatarUrl } from '../../utils/api';
import LeadmeLogo from '../../assets/images/leadme-site-logo.png';
import Avatar from './Avatar';
import Modal from '../Modal';
import QuickAddTaskModal from '../../features/quickadd/QuickAddTaskModal';
import QuickMeetingProjectPicker from './QuickMeetingProjectPicker';
import { usePrislistaPanel } from '../../contexts/PrislistaPanelContext';

function Navbar({ extraButtons, onRefresh }) {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const socket = useSocket();
  // Prislistan är en panel i App, inte en modal här — läget måste överleva att
  // Navbar monteras om vid sidbyte.
  const { oppen: prislistanOppen, vaxla: vaxlaPrislista } = usePrislistaPanel();

  // State för feedback-popup
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState('Bugg');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const feedbackRef = useRef(null);
  const [unresolvedFeedbackCount, setUnresolvedFeedbackCount] = useState(0);

  // Hämta antal olösta feedback-ärenden vid mount + lyssna på socket-events
  useEffect(() => {
    if (!user || (user.role !== 'admin' && !user.hasFeedbackAccess)) return;
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/feedback/unresolved-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setUnresolvedFeedbackCount(data.count || 0);
        }
      } catch (_) {}
    };
    fetchCount();

    if (socket) {
      socket.on('feedback:new', fetchCount);
      socket.on('feedback:updated', fetchCount);
      return () => {
        socket.off('feedback:new', fetchCount);
        socket.off('feedback:updated', fetchCount);
      };
    }
  }, [user, socket]);

  // Stäng feedback-popup vid klick utanför
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target)) {
        setShowFeedback(false);
      }
    };
    if (showFeedback) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFeedback]);

  const handleSendFeedback = async () => {
    if (!feedbackMessage.trim()) return;
    setFeedbackSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: feedbackType,
          description: feedbackMessage,
          pageUrl: window.location.href,
        }),
      });
      if (res.ok) {
        setFeedbackSuccess(true);
        setFeedbackMessage('');
        setFeedbackType('Bugg');
        setTimeout(() => {
          setFeedbackSuccess(false);
          setShowFeedback(false);
        }, 2000);
      }
    } catch (err) {
      console.error('Fel vid skickande av feedback:', err);
    }
    setFeedbackSending(false);
  };

  // State för snabbskapa-modal
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showQuickMeeting, setShowQuickMeeting] = useState(false);

  // State för avatar-modal
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || '');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarError, setAvatarError] = useState('');
  const [avatarSaving, setAvatarSaving] = useState(false);

  const handleLogout = async () => {
    try {
      // Uppdatera lastViewTime innan användaren loggar ut
      if (user && user._id) {
        try {
          const token = localStorage.getItem('token');
          const currentTime = new Date();
          
          // Försök spara senaste visningstid på servern
          const response = await fetch(`${API_URL}/users/lastview`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              lastViewTime: currentTime.toISOString() 
            }),
          });
          
          if (process.env.NODE_ENV === 'development' && response.ok) {
          }
        } catch (error) {
          // Tyst felhantering - vi vill inte hindra utloggning
          if (process.env.NODE_ENV === 'development') {
            // Tyst felhantering vid utloggning
          }
        }
      }
    } finally {
      // Fortsätt med utloggning oavsett resultat
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      navigate('/login');
    }
  };

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  const navigateToHome = () => {
    // Navigera baserat på användarroll
    if (user?.role === 'kund') {
      navigate('/myprojects');
    } else if (['byggkonsult', 'inredningsarkitekt', 'byggare'].includes(user?.role)) {
      navigate('/workspace/oversikt');
    } else if (user?.role === 'admin') {
      navigate('/admin-dashboard');
    }
  };

  const navigateToBookings = () => {
    navigate('/booking-dashboard');
  };

  const navigateToSystem = () => {
    navigate('/dashboard');
  };

  // IA-presentationen öppnas i eget fönster, precis som förstudiepresentationen
  // — poängen är att kunna dela just det fönstret vid skärmdelning utan att
  // portalen syns bakom. Språket byts i presentationens egen footer.
  const oppnaIAPresentation = () => {
    const w = window.screen.availWidth || 1920;
    const h = window.screen.availHeight || 1080;
    const features = `popup=yes,width=${w},height=${h},left=0,top=0,noopener,noreferrer`;
    window.open('/ia-presentation?lang=sv', 'ia-presentation', features);
  };

  // Hantera filuppladdning
  const handleAvatarFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.match('image.*')) {
      setAvatarError('Endast bildfiler är tillåtna');
      return;
    }
    if (file.size > 300 * 1024) {
      setAvatarError('Bilden får inte vara större än 300 KB');
      return;
    }
    setAvatarPreview(URL.createObjectURL(file));
    setAvatarFile(file);
    setAvatarError('');
  };

  // Spara ny avatar
  const handleSaveAvatar = async () => {
    if (!avatarFile) {
      setAvatarError('Välj en bild först');
      return;
    }
    setAvatarSaving(true);
    setAvatarError('');
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('avatar', avatarFile);

      const res = await fetch(`${API_URL}/users/me/avatar-file`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (res.ok && data.avatarUrl) {
        setUser((u) => ({ ...u, avatar: data.avatarUrl }));
        setShowAvatarModal(false);
        setAvatarFile(null);
        setAvatarPreview(data.avatarUrl);
      } else {
        setAvatarError(data.message || 'Kunde inte spara bild');
      }
    } catch (err) {
      setAvatarError('Något gick fel vid uppladdning');
    }
    setAvatarSaving(false);
  };

  // Ta bort avatar
  const handleRemoveAvatar = async () => {
    setAvatarSaving(true);
    setAvatarError('');
    try {
      // Backenden accepterar inte tom sträng, så vi skickar en platshållare
      // men som ändå är kort nog att hantera
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/users/me/avatar`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ avatar: 'REMOVE_AVATAR' }) // Skicka platshållare
      });
      
      if (res.ok) {
        // Uppdatera användaren utan avatar
        setUser((u) => ({ ...u, avatar: null }));
        setShowAvatarModal(false);
        setAvatarFile(null);
        setAvatarPreview('');
      } else {
        const data = await res.json().catch(() => ({}));
        setAvatarError(data.message || 'Kunde inte ta bort bild');
      }
    } catch (err) {
      console.error('Fel vid borttagning av avatar:', err);
      setAvatarError('Något gick fel vid borttagning');
    }
    setAvatarSaving(false);
  };

  // State för mobil-meny
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);

  // Stäng mobil-meny vid klick utanför
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    };
    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  return (
    /* data-app-navbar: de dockade sidopanelerna mäter navbarens underkant för
       att lägga sig precis under den i stället för över. Se DockadePanelerContext. */
    <div data-app-navbar className="bg-white shadow-lg mb-4 sm:mb-8 border-b-2 border-leadme-pink-light" ref={mobileMenuRef}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <div
            onClick={navigateToHome}
            className="cursor-pointer flex items-center"
            aria-label="Gå till startsidan"
            tabIndex="0"
            onKeyDown={(e) => e.key === 'Enter' && navigateToHome()}
          >
            <img
              src={LeadmeLogo}
              alt="LeadMe Logotyp"
              className="h-8 transition-transform duration-200 hover:scale-105"
            />
          </div>

          {/* Desktop nav - dold på mobil */}
          <div className="hidden md:flex items-center space-x-4">
            {user && (
              <>
                {/* UE-navigation: Mina underlag.
                    Egen vy och egen route — byggarens Mina PU renderar ModernPU,
                    som läser /api/pu/my och är spärrad till rollen byggare.
                    Översikten visar bara obesvarade underlag, så det här är
                    vägen till dem UE:n redan svarat på. */}
                {user.role === 'UE' && (
                  <div className="flex items-center space-x-2 mr-4">
                    <button
                      onClick={() => navigate('/workspace/mina-underlag')}
                      className="px-3 py-2 text-sm font-medium text-leadme-gray hover:text-leadme-pink hover:bg-leadme-pink-light/20 rounded-lg transition-colors duration-200"
                      title="Mina PU"
                    >
                      <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Mina PU
                    </button>
                  </div>
                )}

                {/* Byggare-navigation: Mina PU + Behörigheter */}
                {user.role === 'byggare' && (
                  <div className="flex items-center space-x-2 mr-4">
                    <button
                      onClick={() => navigate('/workspace/mina-pu')}
                      className="px-3 py-2 text-sm font-medium text-leadme-gray hover:text-leadme-pink hover:bg-leadme-pink-light/20 rounded-lg transition-colors duration-200"
                      title="Mina PU"
                    >
                      <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Mina PU
                    </button>
                    <button
                      onClick={() => navigate('/workspace/behorigheter')}
                      className="px-3 py-2 text-sm font-medium text-leadme-gray hover:text-leadme-pink hover:bg-leadme-pink-light/20 rounded-lg transition-colors duration-200"
                      title="Behörigheter"
                    >
                      <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Behörigheter
                    </button>
                  </div>
                )}

                {/* Admin/behörighetsnavigation */}
                {(user.role === 'admin' || user.hasBookingAccess || user.hasAdminAccess || user.hasFeedbackAccess || user.hasMarketingAccess) && (
                  <div className="flex items-center space-x-2 mr-4">
                    {user.role !== 'admin' && (
                    <button
                      onClick={navigateToHome}
                      className="px-3 py-2 text-sm font-medium text-leadme-gray hover:text-leadme-pink hover:bg-leadme-pink-light/20 rounded-lg transition-colors duration-200"
                      title="Översikt"
                    >
                      <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                      Översikt
                    </button>
                    )}
                    {(user.role === 'admin' || user.hasBookingAccess) && (
                    <button
                      onClick={navigateToBookings}
                      className="px-3 py-2 text-sm font-medium text-leadme-gray hover:text-leadme-pink hover:bg-leadme-pink-light/20 rounded-lg transition-colors duration-200"
                      title="Bokningshantering"
                    >
                      <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Bokningar
                    </button>
                    )}
                    {(user.role === 'admin' || user.hasFeedbackAccess) && (
                    <button
                      onClick={() => navigate('/admin/feedback')}
                      className="relative px-3 py-2 text-sm font-medium text-leadme-gray hover:text-leadme-pink hover:bg-leadme-pink-light/20 rounded-lg transition-colors duration-200"
                      title="Feedback-hantering"
                    >
                      <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                      Feedback
                      {unresolvedFeedbackCount > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full">
                          {unresolvedFeedbackCount > 99 ? '99+' : unresolvedFeedbackCount}
                        </span>
                      )}
                    </button>
                    )}
                    {(user.role === 'admin' || (user.hasMarketingAccess && (user.marketingTabAccess?.contentPlan || user.marketingTabAccess?.ideer || user.marketingTabAccess?.fotografering))) && (
                    <button
                      onClick={() => navigate('/marketing')}
                      className="px-3 py-2 text-sm font-medium text-leadme-gray hover:text-leadme-pink hover:bg-leadme-pink-light/20 rounded-lg transition-colors duration-200"
                      title="Marknad — foto/recension"
                    >
                      <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Marknad
                    </button>
                    )}
                    {(user.role === 'admin' || user.hasAdminAccess) && (
                    <button
                      onClick={navigateToSystem}
                      className="px-3 py-2 text-sm font-medium text-leadme-gray hover:text-leadme-pink hover:bg-leadme-pink-light/20 rounded-lg transition-colors duration-200"
                      title="Systemhantering"
                    >
                      <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      System
                    </button>
                    )}
                  </div>
                )}
                <NotificationIcon />
                {['byggkonsult', 'inredningsarkitekt', 'byggare'].includes(user.role) && (
                  <button
                    onClick={() => setShowQuickAdd(true)}
                    className="p-2 text-leadme-gray hover:text-leadme-pink hover:bg-leadme-pink-light/20 rounded-full transition-colors duration-200"
                    title={['admin','byggkonsult','byggare'].includes(user.role) ? 'Snabbskapa uppgift eller ÄTA' : 'Snabbskapa uppgift'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                )}
                <QuickAddTaskModal isOpen={showQuickAdd} onClose={() => setShowQuickAdd(false)} />
                {['admin', 'byggkonsult', 'inredningsarkitekt', 'byggare'].includes(user.role) && (
                  <button
                    onClick={() => setShowQuickMeeting(true)}
                    className="p-2 text-leadme-gray hover:text-leadme-pink hover:bg-leadme-pink-light/20 rounded-full transition-colors duration-200"
                    title="Skapa möte"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </button>
                )}
                <QuickMeetingProjectPicker isOpen={showQuickMeeting} onClose={() => setShowQuickMeeting(false)} />
                {['inredningsarkitekt', 'admin'].includes(user.role) && (
                  <button
                    onClick={oppnaIAPresentation}
                    className="p-2 text-leadme-gray hover:text-leadme-pink hover:bg-leadme-pink-light/20 rounded-full transition-colors duration-200"
                    title="Öppna IA-presentationen i nytt fönster"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                    </svg>
                  </button>
                )}
                {['inredningsarkitekt', 'admin'].includes(user.role) && (
                  <button
                    onClick={vaxlaPrislista}
                    className={`p-2 rounded-full transition-colors duration-200 ${
                      prislistanOppen
                        ? 'text-leadme-pink bg-leadme-pink-light/30'
                        : 'text-leadme-gray hover:text-leadme-pink hover:bg-leadme-pink-light/20'
                    }`}
                    title={prislistanOppen ? 'Stäng prislistan' : 'Visa IA-prislistan'}
                    aria-pressed={prislistanOppen}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m-6 4h6m-6 4h4M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={onRefresh}
                  className="p-2 text-leadme-gray hover:text-leadme-pink hover:bg-leadme-pink-light/20 rounded-full transition-colors duration-200"
                  title="Uppdatera sidan"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                {/* Feedback-knapp */}
                <div className="relative" ref={feedbackRef}>
                  <button
                    onClick={() => setShowFeedback(!showFeedback)}
                    className="px-3 py-1.5 bg-leadme-pink text-white text-sm font-medium rounded-lg hover:bg-leadme-pink-dark transition-colors duration-200 flex items-center gap-1"
                    title="Skicka feedback"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                    Feedback
                  </button>
                  {showFeedback && (
                    <div className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-50 p-4">
                      {feedbackSuccess ? (
                        <div className="text-center py-4">
                          <svg className="w-12 h-12 text-green-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm font-medium text-gray-700">Tack för din feedback!</p>
                        </div>
                      ) : (
                        <>
                          <h3 className="text-sm font-semibold text-gray-800 mb-3">Skicka feedback</h3>
                          <div className="mb-3">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Typ</label>
                            <div className="flex gap-2">
                              {['Bugg', 'Förslag', 'Övrigt'].map((typ) => (
                                <button
                                  key={typ}
                                  onClick={() => setFeedbackType(typ)}
                                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                                    feedbackType === typ
                                      ? 'bg-leadme-pink text-white border-leadme-pink'
                                      : 'bg-white text-gray-600 border-gray-300 hover:border-leadme-pink'
                                  }`}
                                >
                                  {typ}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="mb-3">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Beskrivning</label>
                            <textarea
                              value={feedbackMessage}
                              onChange={(e) => setFeedbackMessage(e.target.value)}
                              placeholder="Beskriv problemet eller förslaget..."
                              className="w-full h-24 px-3 py-2 text-sm border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-leadme-pink/30 focus:border-leadme-pink"
                            />
                          </div>
                          <button
                            onClick={handleSendFeedback}
                            disabled={feedbackSending || !feedbackMessage.trim()}
                            className={`w-full py-2 text-sm font-medium rounded-lg transition-colors ${
                              !feedbackMessage.trim() || feedbackSending
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-leadme-pink text-white hover:bg-leadme-pink-dark'
                            }`}
                          >
                            {feedbackSending ? 'Skickar...' : 'Skicka'}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="text-right flex flex-col">
                  <span className="text-sm font-medium text-leadme-gray-dark">
                    {user.firstName} {user.lastName}
                  </span>
                  <span className="text-xs text-leadme-gray">
                    {user.email}
                  </span>
                  <span className="text-xs font-medium text-leadme-gray capitalize">
                    {user.role}
                  </span>
                </div>
                {/* Avatar med modal */}
                <div
                  className="ml-2 relative group"
                  tabIndex={0}
                  aria-label="Byt profilbild"
                  onClick={() => {
                    setShowAvatarModal(true);
                    setAvatarPreview(user.avatar || '');
                    setAvatarFile(null);
                    setAvatarError('');
                  }}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setShowAvatarModal(true)}
                  style={{ outline: 'none', cursor: 'pointer' }}
                >
                  <div className="rounded-full overflow-hidden hover:opacity-80 transition-opacity">
                    <Avatar
                      entity={user}
                      size="md"
                    />
                  </div>
                  <span className="absolute bottom-0 right-0 w-4 h-4 bg-white rounded-full flex items-center justify-center border border-gray-200">
                    <svg className="w-3 h-3 text-leadme-pink" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M4 13V7a2 2 0 012-2h8a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2z" />
                    </svg>
                  </span>
                </div>
                <Modal isOpen={showAvatarModal} onClose={() => setShowAvatarModal(false)}>
                  <div className="flex flex-col items-center p-4">
                    <h2 className="text-lg font-semibold mb-4">Byt profilbild</h2>
                    <div className="w-24 h-24 relative">
                      <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border">
                        {avatarPreview && (avatarPreview.startsWith('blob:') || getAvatarUrl(avatarPreview)) ? (
                          <img
                            src={
                              avatarPreview.startsWith('blob:')
                                ? avatarPreview
                                : getAvatarUrl(avatarPreview)
                            }
                            alt="Avatar"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-2xl font-medium text-gray-500">
                            {user?.firstName?.[0]?.toUpperCase() || ''}{user?.lastName?.[0]?.toUpperCase() || ''}
                          </span>
                        )}
                      </div>
                      {/* Ta bort-knapp på avatarbilden - placerad ovanför bilden */}
                      {avatarPreview && (
                        <button
                          onClick={handleRemoveAvatar}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow hover:bg-red-600 transition-colors"
                          title="Ta bort profilbild"
                          aria-label="Ta bort profilbild"
                          tabIndex={0}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <div className="mb-4 mt-4"></div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarFileChange}
                      className="block w-full text-sm text-gray-500 mb-2"
                    />
                    {avatarError && <div className="text-red-500 text-sm mb-2">{avatarError}</div>}
                    <div className="flex w-full space-x-2 mt-2">
                      <button
                        onClick={() => setShowAvatarModal(false)}
                        className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                        type="button"
                      >
                        Avbryt
                      </button>

                      <button
                        onClick={handleSaveAvatar}
                        disabled={avatarSaving || !avatarFile}
                        className={`flex-1 ${!avatarFile ? 'bg-leadme-pink/50' : 'bg-leadme-pink hover:bg-leadme-pink-dark'} text-white py-2 rounded-lg transition-colors`}
                        type="button"
                      >
                        {avatarSaving ? 'Sparar...' : 'Spara'}
                      </button>
                    </div>
                  </div>
                </Modal>
              </>
            )}
            {extraButtons}
            <button
              onClick={handleSettingsClick}
              className="p-2 text-leadme-gray hover:text-leadme-pink rounded-full hover:bg-leadme-pink-light/20"
              title="Inställningar"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            <button
              onClick={handleLogout}
              className="px-2.5 py-1.5 sm:px-3 text-sm font-medium bg-leadme-red text-white rounded-lg hover:bg-leadme-red-dark transition-colors"
            >
              Logga ut
            </button>
          </div>

          {/* Mobil nav - synlig på <md */}
          <div className="flex md:hidden items-center space-x-2">
            {user && <NotificationIcon />}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-leadme-gray hover:text-leadme-pink rounded-lg transition-colors"
              aria-label="Öppna meny"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobil drawer */}
        {mobileMenuOpen && user && (
          <div className="md:hidden border-t border-gray-200 py-3 space-y-2">
            {/* Användarinfo */}
            <div className="flex items-center gap-3 px-2 py-2">
              <div className="rounded-full overflow-hidden flex-shrink-0"
                onClick={() => {
                  setShowAvatarModal(true);
                  setAvatarPreview(user.avatar || '');
                  setAvatarFile(null);
                  setAvatarError('');
                  setMobileMenuOpen(false);
                }}
              >
                <Avatar entity={user} size="md" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-leadme-gray-dark truncate">{user.firstName} {user.lastName}</p>
                <p className="text-xs text-leadme-gray truncate">{user.email}</p>
                <p className="text-xs font-medium text-leadme-gray capitalize">{user.role}</p>
              </div>
            </div>

            <div className="border-t border-gray-100 my-1"></div>

            {/* Byggare-knappar: Mina PU + Behörigheter */}
            {user.role === 'byggare' && (
              <>
                <button
                  onClick={() => { navigate('/workspace/mina-pu'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-leadme-gray hover:bg-leadme-pink-light/20 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Mina PU
                </button>
                <button
                  onClick={() => { navigate('/workspace/behorigheter'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-leadme-gray hover:bg-leadme-pink-light/20 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Behörigheter
                </button>
              </>
            )}

            {/* Admin/behörighetsknappar */}
            {(user.role === 'admin' || user.hasBookingAccess || user.hasAdminAccess || user.hasFeedbackAccess || user.hasMarketingAccess) && (
              <>
                {user.role !== 'admin' && (
                <button
                  onClick={() => { navigateToHome(); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-leadme-gray hover:bg-leadme-pink-light/20 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  Översikt
                </button>
                )}
                {['admin', 'byggkonsult', 'inredningsarkitekt', 'byggare'].includes(user.role) && (
                <button
                  onClick={() => { setShowQuickMeeting(true); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-leadme-gray hover:bg-leadme-pink-light/20 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Skapa möte
                </button>
                )}
                {['inredningsarkitekt', 'admin'].includes(user.role) && (
                <button
                  onClick={() => { oppnaIAPresentation(); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-leadme-gray hover:bg-leadme-pink-light/20 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                  </svg>
                  IA-presentation
                </button>
                )}
                {['inredningsarkitekt', 'admin'].includes(user.role) && (
                <button
                  onClick={() => { vaxlaPrislista(); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-leadme-gray hover:bg-leadme-pink-light/20 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m-6 4h6m-6 4h4M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  {prislistanOppen ? 'Stäng prislistan' : 'Prislista'}
                </button>
                )}
                {(user.role === 'admin' || user.hasBookingAccess) && (
                <button
                  onClick={() => { navigateToBookings(); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-leadme-gray hover:bg-leadme-pink-light/20 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Bokningar
                </button>
                )}
                {(user.role === 'admin' || user.hasFeedbackAccess) && (
                <button
                  onClick={() => { navigate('/admin/feedback'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-leadme-gray hover:bg-leadme-pink-light/20 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  Feedback
                  {unresolvedFeedbackCount > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full">
                      {unresolvedFeedbackCount > 99 ? '99+' : unresolvedFeedbackCount}
                    </span>
                  )}
                </button>
                )}
                {(user.role === 'admin' || (user.hasMarketingAccess && (user.marketingTabAccess?.contentPlan || user.marketingTabAccess?.ideer || user.marketingTabAccess?.fotografering))) && (
                <button
                  onClick={() => { navigate('/marketing'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-leadme-gray hover:bg-leadme-pink-light/20 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Marknad
                </button>
                )}
                {(user.role === 'admin' || user.hasAdminAccess) && (
                <button
                  onClick={() => { navigateToSystem(); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-leadme-gray hover:bg-leadme-pink-light/20 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  System
                </button>
                )}
              </>
            )}

            {/* Inställningar */}
            <button
              onClick={() => { handleSettingsClick(); setMobileMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-leadme-gray hover:bg-leadme-pink-light/20 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Inställningar
            </button>

            {/* Logga ut */}
            <button
              onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-leadme-red font-medium hover:bg-red-50 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logga ut
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Navbar;